import React, { useEffect, useRef, useState } from 'react';
import { FiCalendar, FiChevronDown, FiFilter, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RANGE_OPTIONS } from './dateRange';
import { PMS_OPTIONS } from './derive';
import { US_STATE_CODES, stateName } from './usStates';

// Filter bar. Only the filters that apply to the active tab are shown so
// the bar stays short and every control means something for what's on
// screen. Range + State are global; everything else is contextual.

export const DEFAULT_FILTERS = {
  range: '30d',
  customStart: null,
  customEnd: null,
  state: 'all',
  pms: 'all',
  hostLifecycle: 'all',
  orderStatus: 'all',
  deliveryFeeType: 'all',
  storeLocation: '',
  search: '',
};

const HOST_LIFECYCLE_OPTIONS = [
  { id: 'all', label: 'All hosts' },
  { id: 'new', label: 'New (30d)' },
  { id: 'legacy', label: 'Legacy' },
];

const ORDER_STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'created', label: 'Created' },
  { id: 'paymentCompleted', label: 'Payment completed' },
  { id: 'requested', label: 'Requested' },
  { id: 'pending', label: 'Pending' },
  { id: 'picked', label: 'Picked' },
  { id: 'completed', label: 'Completed' },
];

const DELIVERY_TYPE_OPTIONS = [
  { id: 'all', label: 'All delivery types' },
  { id: 'deliver_to_door', label: 'Deliver to door' },
  { id: 'pre_arrival_stocking', label: 'Pre-arrival stocking' },
  { id: 'pre_arrival_stocking_plus_door', label: 'Stocking + door' },
];

const STATE_OPTIONS = [
  { id: 'all', label: 'All states' },
  ...US_STATE_CODES.map((code) => ({ id: code, label: `${code} · ${stateName(code)}` })),
];

function useClickAway(ref, onAway) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      onAway();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onAway, ref]);
}

function CompactSelect({ value, onChange, options, label, widthClass = 'w-52', dirty = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => setOpen(false));
  const current = options.find((o) => o.id === value) || options[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-8 items-center gap-1.5 rounded-lg border bg-white px-2.5 text-xs font-medium transition-colors duration-150 hover:bg-zinc-50 motion-reduce:transition-none ${
          dirty ? 'border-zinc-900 text-zinc-900' : 'border-zinc-200 text-zinc-700'
        }`}
      >
        <span className="text-zinc-400">{label}</span>
        <span className="max-w-[140px] truncate text-zinc-900">{current.label}</span>
        <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
      </button>
      {open ? (
        <div className={`absolute left-0 z-30 mt-1 max-h-72 overflow-auto rounded-lg border border-zinc-200 bg-white shadow-lg ${widthClass}`}>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-zinc-50 ${
                opt.id === value ? 'bg-zinc-50 font-semibold text-zinc-900' : 'text-zinc-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ChipGroup({ title, options, value, onChange }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`rounded-md px-2 py-1 text-[11px] font-medium ${
              value === opt.id ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function countActiveFilters(filters, view) {
  let count = 0;
  if (filters.range !== DEFAULT_FILTERS.range) count += 1;
  if (filters.state !== 'all') count += 1;
  if (filters.search) count += 1;
  if (view === 'orders') {
    if (filters.orderStatus !== 'all') count += 1;
    if (filters.deliveryFeeType !== 'all') count += 1;
    if (filters.storeLocation) count += 1;
  }
  if (view === 'hosts' || view === 'properties') {
    if (filters.pms !== 'all') count += 1;
    if (filters.hostLifecycle !== 'all') count += 1;
  }
  return count;
}

export default function DashboardHeader({ activeView, filters, onChange, onReset, onRefresh, refreshing, rangeLabel }) {
  const [storeInput, setStoreInput] = useState(filters.storeLocation);
  const [searchInput, setSearchInput] = useState(filters.search);
  const [moreOpen, setMoreOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const moreRef = useRef(null);
  const customRef = useRef(null);
  useClickAway(moreRef, () => setMoreOpen(false));
  useClickAway(customRef, () => setCustomOpen(false));

  useEffect(() => setStoreInput(filters.storeLocation), [filters.storeLocation]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (storeInput !== filters.storeLocation) onChange({ storeLocation: storeInput });
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.storeLocation, onChange, storeInput]);

  // Global search is debounced so every keystroke doesn't fire a summary +
  // records request, but still feels instant ("725" shows hits right away).
  useEffect(() => setSearchInput(filters.search), [filters.search]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) onChange({ search: searchInput });
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.search, onChange, searchInput]);

  const showOrderFilters = activeView === 'orders';
  const showHostFilters = activeView === 'hosts' || activeView === 'properties';
  const orderAdvancedCount =
    (filters.orderStatus !== 'all' ? 1 : 0) + (filters.deliveryFeeType !== 'all' ? 1 : 0) + (filters.storeLocation ? 1 : 0);
  const activeCount = countActiveFilters(filters, activeView);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
      <div className="relative w-full laptop:w-72 desktop:w-80">
        <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search hosts, properties, orders, drivers…"
          aria-label="Search dashboard"
          className={`h-8 w-full rounded-lg border bg-white pl-8 pr-7 text-xs text-zinc-900 outline-none transition-colors duration-150 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 motion-reduce:transition-none ${
            filters.search ? 'border-zinc-900' : 'border-zinc-200'
          }`}
        />
        {searchInput ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              onChange({ search: '' });
            }}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
        {RANGE_OPTIONS.map((opt) => {
          const active = filters.range === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange({ range: opt.id });
                if (opt.id === 'custom') setCustomOpen(true);
              }}
              title={opt.longLabel}
              className={`h-7 rounded-md px-2.5 text-xs font-medium transition-colors duration-150 motion-reduce:transition-none ${
                active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {filters.range === 'custom' ? (
        <div className="relative" ref={customRef}>
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiCalendar className="h-3.5 w-3.5 text-zinc-400" />
            {filters.customStart && filters.customEnd ? rangeLabel : 'Pick dates'}
          </button>
          {customOpen ? (
            <div className="absolute left-0 z-40 mt-1 rounded-lg border border-zinc-200 bg-white p-3 shadow-lg">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-medium text-zinc-600">Start</label>
                <DatePicker
                  selected={filters.customStart}
                  onChange={(d) => onChange({ customStart: d })}
                  selectsStart
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
                <label className="text-[11px] font-medium text-zinc-600">End</label>
                <DatePicker
                  selected={filters.customEnd}
                  onChange={(d) => onChange({ customEnd: d })}
                  selectsEnd
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  minDate={filters.customStart}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <CompactSelect
        label="State"
        value={filters.state}
        onChange={(value) => onChange({ state: value })}
        options={STATE_OPTIONS}
        widthClass="w-56"
        dirty={filters.state !== 'all'}
      />

      {showHostFilters ? (
        <>
          <CompactSelect
            label="PMS"
            value={filters.pms}
            onChange={(value) => onChange({ pms: value })}
            options={PMS_OPTIONS}
            widthClass="w-44"
            dirty={filters.pms !== 'all'}
          />
          <CompactSelect
            label="Lifecycle"
            value={filters.hostLifecycle}
            onChange={(value) => onChange({ hostLifecycle: value })}
            options={HOST_LIFECYCLE_OPTIONS}
            widthClass="w-40"
            dirty={filters.hostLifecycle !== 'all'}
          />
        </>
      ) : null}

      {showOrderFilters ? (
        <div className="relative" ref={moreRef}>
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg border bg-white px-2.5 text-xs font-medium hover:bg-zinc-50 ${
              orderAdvancedCount ? 'border-zinc-900 text-zinc-900' : 'border-zinc-200 text-zinc-700'
            }`}
          >
            <FiFilter className="h-3.5 w-3.5 text-zinc-400" />
            Order filters
            {orderAdvancedCount ? (
              <span className="rounded-full bg-zinc-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">{orderAdvancedCount}</span>
            ) : null}
            <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
          </button>
          {moreOpen ? (
            <div className="absolute left-0 z-30 mt-1 w-80 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg">
              <div className="space-y-4 p-3">
                <ChipGroup title="Order status" options={ORDER_STATUS_OPTIONS} value={filters.orderStatus} onChange={(v) => onChange({ orderStatus: v })} />
                <ChipGroup title="Delivery type" options={DELIVERY_TYPE_OPTIONS} value={filters.deliveryFeeType} onChange={(v) => onChange({ deliveryFeeType: v })} />
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Store</p>
                  <input
                    type="search"
                    value={storeInput}
                    onChange={(e) => setStoreInput(e.target.value)}
                    placeholder="Search store location"
                    className="h-8 w-full rounded-lg border border-zinc-200 bg-white px-3 text-xs text-zinc-900 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        <span className="hidden text-[11px] text-zinc-500 laptop:inline">{rangeLabel}</span>
        {activeCount ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiX className="h-3.5 w-3.5 text-zinc-400" />
            Reset
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex h-8 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          title="Reload dashboard data"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  );
}
